import { TenantConfigViewItem, TenantConfigViewData } from 'src/dao/tenantConfigClient';
import { Option } from 'src/components/Configure/ConfigureModal';
import { setActivePage } from 'src/pages/NavigationShell/NavigationShell.slice';
import { receiveWorklistConfig, updateSelectedLevel } from 'src/pages/Worklist/Worklist.slice';
import { receivePricingOverTimeConfig } from 'src/pages/AssortmentBuild/Pricing/PricingOverTime.slice';
import { receiveGridViewConfig } from 'src/pages/Hindsighting/StyleColorReview/GridView/GridView.slice';
import { receiveStyleEditConfigs } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.slice';
import { receiveTenantConfig as receiveStyleByLevelOvertimeConfig } from 'src/pages/AssortmentBuild/OvertimeView/StyleByLevel/StyleByLevelOvertime.slice';
import { parseSortConfig } from 'src/utils/Config/Sort';
import { get, cloneDeep, isNil, isNumber } from 'lodash';
import { FlowStatus } from 'configuration-schemas/bindings.types';
import {
  FavoriteResponseItem,
  ValidatedAssortmentFavorite,
  ValidatedFavoriteResponse,
} from 'src/components/Subheader/Favorites/Favorites.types';
import { createAsyncThunk, createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import ServiceContainer from 'src/ServiceContainer';
import type { ExtraPivotParam, SubheaderDefnProps } from 'configuration-schemas/confdefnComponentProps';
import { SortByDirection } from 'src/components/Subheader/Subheader.types';
import { BasicItem } from 'src/worker/pivotWorker.types';
import { Option as DropdownOption } from 'src/components/Subheader/Subheader';
import { AppState } from 'src/store';
import { getValidValues } from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/StyleEditSection.client';
import { getUrl } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import { logError } from 'src/services/loggingService';
import { AppType } from 'configuration-schemas/general';
import { receiveFlowSheetByStyleConfig } from 'src/pages/AssortmentBuild/FlowSheet/FlowSheetByStyle.slice';
import { receiveConfig } from 'src/pages/Hindsighting/HistoryGrid/ListView/ListView.slice';

export interface SubheaderViewDefns extends SubheaderDefnProps {}

export interface SubheaderDefns {
  groupBy: DropdownConfigViewData;
  sortBy: DropdownConfigViewData;
  countLimit: DropdownConfigViewData;
  pareDown: DropdownConfigViewData;
}

// FIXME: do we need to include extra props here if only manipulating groupByOptions?
export interface OverLoadSubheaderProps {
  title?: 'Grid View' | 'Quick Snapshot';
  showFlowStatus?: boolean;
  showSearch: true;
  groupByOptions?: DropdownConfigViewData;
}

export interface SubheaderDropdownSlice {
  defaultSelection: number;
  selection: number | null;
  selectionDataIndex?: string | null;
  options: TenantConfigViewItem[];
  refreshOnChange?: boolean;
}

export interface DropdownConfigViewData extends TenantConfigViewData {
  hideEmptyRow?: boolean;
  refreshOnChange?: boolean;
}

export interface GroupBySlice extends SubheaderDropdownSlice {}

export interface SortBySlice extends SubheaderDropdownSlice {
  direction: SortByDirection;
}
export interface GroupByPayload {
  options: TenantConfigViewItem[];
  selection: number | null;
}
export interface PareDownSlice {
  defaultSelections: TenantConfigViewItem[];
  selections: TenantConfigViewItem[];
  options: TenantConfigViewItem[];
}

export interface SubheaderSlice {
  title?: string;
  showFlowStatus?: boolean;
  showSearch?: true;
  search: string;
  altSearch: string;
  groupBy: GroupBySlice;
  groupByDefn?: TenantConfigViewData;
  sortBy: SortBySlice;
  sortByDefn?: TenantConfigViewData;
  pareDown: PareDownSlice;
  pareDownDefn?: TenantConfigViewData;
  lookBackPeriod: string;
  countLimit?: number;
  countLimitOptions?: number[];
  countLimitDefault?: number;
  subheaderLoading: boolean;
  configureSelections?: Option[];
  flowStatus: number[];
  flowStatusConfig?: FlowStatus;
  altFlowStatus: number[];
  /** @deprecated use `favorites` instead of `favoritesList` */
  favoritesList?: FavoriteResponseItem[];
  favorites: ValidatedFavoriteResponse[];
  activeFavorite: ValidatedAssortmentFavorite | null;
  possibleBreadcrumbs: string[] | null;
  // topMemberDropdown props
  topMemberOptions?: BasicItem[];
  topMemberSelection?: string | undefined;
  //for snapshots etc
  extraPivotParamsConfig?: Record<string, ExtraPivotParam>;
  extraPivotParamsOptions?: Record<string, DropdownOption[]>;
  extraPivotParamsSelections: Record<string, string | number | null>;
  activeWorkflowKey: string | null;
  // Companion list selections – persisted globally across views when no favorite is active
  companionSortField: string | null;
  companionSortDirection: SortByDirection;
  companionLevelField: string | null;
}

export const emptyItem = {
  dataIndex: '',
  text: '',
  dimension: 'product',
};

export const initialGroupBy: GroupBySlice = {
  defaultSelection: 0,
  selection: null,
  selectionDataIndex: null,
  options: [emptyItem],
};

export const initialSortBy: SortBySlice = {
  defaultSelection: 0,
  selection: null,
  direction: 'desc',
  options: [emptyItem],
};

export const initialPareDown = {
  defaultSelections: [],
  selections: [],
  options: [emptyItem],
};

const initialState: SubheaderSlice = {
  search: '',
  altSearch: '',
  groupBy: initialGroupBy,
  sortBy: initialSortBy,
  pareDown: initialPareDown,
  lookBackPeriod: 'YTD',
  flowStatus: [],
  altFlowStatus: [],
  subheaderLoading: false,
  flowStatusConfig: {} as FlowStatus,
  favoritesList: [],
  favorites: [],
  activeFavorite: null,
  possibleBreadcrumbs: null,
  // topMemberDropdown props
  topMemberOptions: [],
  topMemberSelection: undefined,
  extraPivotParamsConfig: {},
  extraPivotParamsOptions: {},
  extraPivotParamsSelections: {},
  activeWorkflowKey: null,
  companionSortField: null,
  companionSortDirection: 'desc',
  companionLevelField: null,
};

export const emptySubheaderState = cloneDeep(initialState);

export function _handleDropdownExtras(
  ddSlice: SubheaderDropdownSlice,
  viewDefn: DropdownConfigViewData,
  currentSelection: TenantConfigViewItem | null,
) {
  ddSlice.options = viewDefn.view;
  if (viewDefn.hideEmptyRow !== true) {
    ddSlice.options.unshift(emptyItem);
  }
  if (viewDefn.default) {
    const defaultSelection = ddSlice.options.findIndex((opt) => opt.dataIndex === viewDefn.default);
    ddSlice.defaultSelection = defaultSelection;
  }
  let newSelection = -1;
  if (!isNil(currentSelection)) {
    newSelection = ddSlice.options.findIndex((opt) => opt.dataIndex === currentSelection.dataIndex);
  }
  if (newSelection >= 0) {
    ddSlice.selection = newSelection;
  } else {
    ddSlice.selection = null;
  }
  if (viewDefn.refreshOnChange) {
    ddSlice.refreshOnChange = viewDefn.refreshOnChange;
  }
  return ddSlice;
}

/**
 * Returns true when there's an active favorite in either the new `favorites` collection
 * or the deprecated `favoritesList`. Used by view-config extraReducer cases to skip
 * overwriting companion-list defaults while a favorite is driving them.
 */
function hasActiveFavorite(state: SubheaderSlice): boolean {
  return !isNil(state.favorites.find((fav) => fav.active)) || !isNil(state.favoritesList?.find((f) => f.active));
}

/**
 * Check if the old sort selection is in the new options,
 * if it is, keep it and the direction.
 * If it isn't, reset to default and set direction to the default which is always 'desc'
 */
function reconcileCompanionSortField(
  state: SubheaderSlice,
  incomingOptions: { dataIndex: string }[],
  incomingDefault: string | null | undefined,
): void {
  const stillValid =
    !isNil(state.companionSortField) && incomingOptions.some((opt) => opt.dataIndex === state.companionSortField);
  state.companionSortField = stillValid ? state.companionSortField : (incomingDefault ?? null);
  state.companionSortDirection = stillValid ? state.companionSortDirection : 'desc';
}

/**
 * Check if the old level selection is in the new options,
 * if it is, keep it
 * If it isn't, reset to default
 */
function reconcileCompanionLevelField(
  state: SubheaderSlice,
  incomingOptions: { dataIndex: string }[],
  incomingDefault: string | null | undefined,
): void {
  const stillValid =
    !isNil(state.companionLevelField) && incomingOptions.some((opt) => opt.dataIndex === state.companionLevelField);
  state.companionLevelField = stillValid ? state.companionLevelField : (incomingDefault ?? null);
}

/**
 * Processes groupBy properties `hideEmptyRow` and `refreshOnChange` options from config.
 * */
function applyViewDefnToSlice(slice: SubheaderDropdownSlice, viewDefn: DropdownConfigViewData): SubheaderDropdownSlice {
  const { hideEmptyRow, refreshOnChange } = viewDefn;
  const copy = cloneDeep(slice);

  if (hideEmptyRow !== true) {
    copy.options.unshift(emptyItem);
  }

  if (refreshOnChange) {
    copy.refreshOnChange = refreshOnChange;
  }

  return copy;
}

const subheaderSlice = createSlice({
  name: 'subheader',
  initialState: initialState,
  reducers: {
    requestViewDefns: (state, _action: PayloadAction<string[]>) => {
      // this action requires a payload, but it is used in various containers,
      // and isn't meant to go into state
      state.subheaderLoading = true;
    },
    receiveViewDefns: (state, action: PayloadAction<SubheaderDefns>) => {
      const defns = action.payload;

      //#region process groupBy viewdefn
      let groupBy: GroupBySlice = cloneDeep(initialGroupBy);
      const groupByDefn = defns.groupBy;

      if (groupByDefn) {
        // TODO: when we decide to use string value instead of index everywhere this condition can be removed
        // this really sucks to have to do this but it is isolated here
        // number => set as value or string => find index of matching dataIndex
        const defaultIndex = isNumber(groupByDefn.default)
          ? groupByDefn.default
          : groupByDefn.view.findIndex((opt) => opt.dataIndex === groupByDefn.default);

        // always try to set the defaultIndex from config, falling back to 0 if unset
        const defaultSelection = defaultIndex >= 0 ? defaultIndex : 0;

        // setup new viewDefn's groupBy options + default selection
        const unprocessedGroupBy: GroupBySlice = {
          ...groupBy,
          options: groupByDefn.view,
          defaultSelection,
        };
        groupBy = applyViewDefnToSlice(unprocessedGroupBy, groupByDefn);
        const previousSelectionDataIndex = state.groupBy.selectionDataIndex;

        if (!isNil(previousSelectionDataIndex)) {
          // we have a previous selection, need to find its index in the current viewDefn's options
          const split = previousSelectionDataIndex.split(':');
          const formattedDataIndex = split.length === 1 ? split[0] : split[1];

          const nextGroupByIndex = groupBy.options.findIndex((gb) => {
            // need to compare raw values instead of formatted values since the format can vary by viewDefn
            // i.e. member:class:name => class or level:subclass:name => subclass
            const gbSplit = gb.dataIndex.split(':');

            // take raw item only, middle item if formatted
            const formattedGbDataIndex = gbSplit.length === 1 ? gbSplit[0] : gbSplit[1];

            return formattedDataIndex === formattedGbDataIndex;
          });

          // validate previous selection is in nextGroupby's options
          const isValid = nextGroupByIndex >= 0;

          groupBy = {
            ...groupBy,
            // only set selection if previous selection is a valid option,
            // otherwise keep null since falling back to default
            selection: isValid ? nextGroupByIndex : null,
            selectionDataIndex: isValid ? groupBy.options[nextGroupByIndex].dataIndex : null,
          };
        }
      }
      //#endregion

      //#region process sortBy viewDefn
      // TODO: probably want to make sortBy work like groupBy above, with selection validation across views
      let sortBy: SortBySlice = { ...cloneDeep(initialSortBy), direction: state.sortBy.direction || 'desc' };
      if (defns.sortBy) {
        const curSel = get(state.sortBy.options, `[${state.sortBy.selection}]`, null);
        const sortByDefn = defns.sortBy;
        sortBy = {
          ...state.sortBy,
          ..._handleDropdownExtras(sortBy, sortByDefn, curSel),
        };
      }
      //#endregion

      //#region process pareDown
      let pareDown: PareDownSlice = cloneDeep(initialPareDown);
      if (defns.pareDown) {
        const defaultText = (defns.pareDown as any).default || [];
        const pareDownDefn = defns.pareDown;
        const selections = pareDownDefn.view.filter((x) => defaultText.indexOf(x.text) != -1);
        pareDown = {
          ...state.pareDown,
          selections,
          defaultSelections: selections,
          options: pareDownDefn.view,
        };
      }
      //#endregion

      //#region process countLimit
      let countLimitOptions;
      let countLimit;

      if (defns.countLimit) {
        countLimitOptions = defns.countLimit.options;
        // If there is already a count limit selected and it's available in limit options, keep it
        if (state.countLimit && countLimitOptions && countLimitOptions.includes(state.countLimit)) {
          countLimit = state.countLimit;
        } else {
          countLimit = defns.countLimit.default as number;
        }
      }
      //#endregion

      if (!groupByDefn) {
        return {
          ...state,
          sortBy,
          countLimit,
          countLimitOptions,
          countLimitDefault: defns.countLimit ? (defns.countLimit.default as number) : 0,
          subheaderLoading: false,
          groupByDefn: defns.groupBy,
          sortByDefn: defns.sortBy,
          pareDownDefn: defns.pareDown,
          pareDown,
        };
      }

      return {
        ...state,
        groupBy,
        sortBy,
        countLimit,
        countLimitOptions,
        countLimitDefault: defns.countLimit ? (defns.countLimit.default as number) : 0,
        subheaderLoading: false,
        groupByDefn: defns.groupBy,
        sortByDefn: defns.sortBy,
        pareDownDefn: defns.pareDown,
        pareDown,
      };
    },
    updateSearch: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
    },
    updateAlternateSearch: (state, action: PayloadAction<string>) => {
      state.altSearch = action.payload;
    },
    updateConfigureSelections: (state, action: PayloadAction<Option[]>) => {
      state.configureSelections = action.payload;
    },
    updateFlowStatus: (state, action: PayloadAction<number[]>) => {
      state.flowStatus = action.payload;
    },
    updateFlowStatusConfig: (state, action: PayloadAction<FlowStatus>) => {
      state.flowStatusConfig = action.payload;
    },
    updateAlternateFlowStatus: (state, action: PayloadAction<number[]>) => {
      state.altFlowStatus = action.payload;
    },
    updateLookBackPeriod: (state, action: PayloadAction<string>) => {
      state.lookBackPeriod = action.payload;
    },
    updateGroupBy: (state, action: PayloadAction<(number | null) | GroupByPayload>) => {
      let nextGroupBy;
      if (isNil(action.payload)) {
        nextGroupBy = {
          ...state.groupBy,
          selection: null,
          selectionDataIndex: null,
        };
      } else if (isNumber(action.payload)) {
        // grab the incoming index and make sure its in range
        const idx = action.payload;
        const option = idx >= 0 && idx < state.groupBy.options.length ? state.groupBy.options[idx] : undefined;

        nextGroupBy = {
          ...state.groupBy,
          // only keep the index if it points at a real option
          selection: option ? idx : null,
          // only read dataIndex when opt exists
          selectionDataIndex: option?.dataIndex ?? null,
        };
      } else {
        nextGroupBy = {
          ...state.groupBy,
          options: action.payload.options,
          selection: action.payload.selection,
          selectionDataIndex:
            action.payload.selection && action.payload.selection >= 0
              ? action.payload.options[action.payload.selection].dataIndex
              : null,
        };
      }

      state.groupBy = nextGroupBy;
    },
    updateTopMemberSelection: (state, action: PayloadAction<string>) => {
      state.topMemberSelection = action.payload;
    },
    receiveTopMemberDropdownData(state, action: PayloadAction<BasicItem[]>) {
      state.topMemberOptions = action.payload;
      if (!isNil(state.topMemberSelection)) {
        const foundSelection = state.topMemberOptions.find((i) => i.id === state.topMemberSelection);
        if (!foundSelection) state.topMemberSelection = state.topMemberOptions[0].id;
      }
    },
    updateSortBy: (state, action: PayloadAction<number | null>) => {
      state.sortBy = {
        ...state.sortBy,
        selection: action.payload,
      };
    },
    updatePareDown: (state, action: PayloadAction<TenantConfigViewItem[]>) => {
      state.pareDown = {
        ...state.pareDown,
        selections: action.payload,
      };
    },
    updateCountLimit: (state, action: PayloadAction<number>) => {
      state.countLimit = action.payload;
    },
    /** @deprecated see updateFavorites instead */
    updateFavoritesList: (state, action: PayloadAction<FavoriteResponseItem[]>) => {
      state.favoritesList = action.payload;

      const activeFavorite = action.payload.find((fav) => fav.active);
      const companionData = activeFavorite?.jsonBlob?.companionData;
      const groupByDataIndex = activeFavorite?.jsonBlob?.groupByDataIndexSelection ?? null;
      // TODO move all this logic to `updateFavorites`
      if (hasActiveFavorite(state) && companionData) {
        const { companionSortField, companionSortDirection, companionLevelField } = companionData;
        if (companionSortField) {
          state.companionSortField = companionSortField;
        }
        if (companionSortDirection) {
          state.companionSortDirection = companionSortDirection;
        }
        if (companionLevelField) {
          state.companionLevelField = companionLevelField;
        }
      }

      if (!isNil(groupByDataIndex)) {
        const idx = state.groupBy.options.findIndex((opt) => opt.dataIndex === groupByDataIndex);
        state.groupBy = {
          ...state.groupBy,
          selection: idx >= 0 ? idx : null,
          selectionDataIndex: idx >= 0 ? state.groupBy.options[idx].dataIndex : null,
        };
      }
    },
    updateFavorites: (state, action: PayloadAction<ValidatedFavoriteResponse[]>) => {
      const activeFavorite = action.payload.find((fav) => fav.active);
      state.activeFavorite = isNil(activeFavorite) ? null : activeFavorite.jsonBlob;
      state.favorites = action.payload;
    },
    updateSortByDirection: (state) => {
      if (state.sortBy.direction === 'asc') {
        state.sortBy.direction = 'desc';
      } else {
        state.sortBy.direction = 'asc';
      }
    },
    maybeUpdateSortByDirection: (state, action: PayloadAction<string>) => {
      if (state.sortBy.direction !== action.payload) {
        if (state.sortBy.direction === 'asc') {
          state.sortBy.direction = 'desc';
        } else {
          state.sortBy.direction = 'asc';
        }
      }
    },
    // TODO: The only logic that is needed in this action is the hideEmptyRow logic,
    // everything else (title, boolean values) are not relevant.
    // This should be moved to the receiveViewDefns action handler.
    // For more details see https://s5stratosdev.atlassian.net/browse/INT-1436
    overloadSubheader: (state, action: PayloadAction<OverLoadSubheaderProps>) => {
      const { groupByOptions, ...newProps } = action.payload;

      let options: TenantConfigViewItem[];
      if (!isNil(groupByOptions)) {
        options = groupByOptions?.hideEmptyRow ? [...groupByOptions.view] : [emptyItem, ...groupByOptions.view];
      } else {
        options = [];
      }

      return {
        ...state,
        ...newProps,
        groupBy: {
          ...state.groupBy,
          options,
        },
      };
    },
    receiveError: (_state, action: PayloadAction<string>) => {
      // mild side effects here
      ServiceContainer.loggingService.error(`An error occured in the subheader: ${action.payload}`);
      return initialState;
    },
    setPossibleBreadcrumbs: (state, action: PayloadAction<string[] | null>) => {
      state.possibleBreadcrumbs = action.payload;
    },
    setExtraPivotParamsConfig: (state, action: PayloadAction<Record<string, ExtraPivotParam>>) => {
      state.extraPivotParamsConfig = action.payload;
    },
    setExtraPivotParamsOptions: (state, action: PayloadAction<Record<string, DropdownOption[]>>) => {
      state.extraPivotParamsOptions = {
        ...state.extraPivotParamsOptions,
        ...action.payload,
      };
    },
    clearExtraPivotParams: (state) => {
      state.extraPivotParamsOptions = {};
      state.extraPivotParamsConfig = {};
    },
    setExtraPivotParamsSelection: (state, action: PayloadAction<{ key: string; value: string | number | null }>) => {
      const { key, value } = action.payload;
      state.extraPivotParamsSelections[key] = value;
    },
    updateCompanionSortField: (state, action: PayloadAction<string | null>) => {
      state.companionSortField = action.payload;
    },
    updateCompanionSortDirection: (state, action: PayloadAction<SortByDirection>) => {
      state.companionSortDirection = action.payload;
    },
    updateCompanionLevelByField: (state, action) => {
      state.companionLevelField = action.payload?.dataIndex ?? null;
    },
    cleanUp: () => {
      return initialState;
    },
    setActiveWorkflowKey: (state, action: PayloadAction<{ key: string | null; clearsSearch: boolean }>) => {
      const prevKey = state.activeWorkflowKey;
      const nextKey = action.payload.key;

      // Only clear if:
      //  - the new workflow is configured to clear search
      //  - AND its different from the previous workflow
      if (action.payload.clearsSearch && prevKey !== nextKey) {
        state.search = '';
        state.altSearch = '';
      }

      state.activeWorkflowKey = nextKey;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setActivePage, (state) => {
      state.configureSelections = undefined;
    });
    builder.addCase(updateSelectedLevel, (state, action) => {
      state.companionLevelField = action.payload?.dataIndex ?? null;
    });
    builder.addCase(receivePricingOverTimeConfig, (state, action) => {
      if (hasActiveFavorite(state)) return;
      const { groupByItems, defaultCompanionLevelField, defaultCompanionSortField, listSort } = action.payload;
      state.groupBy = { ...state.groupBy, options: groupByItems ?? [] };
      reconcileCompanionLevelField(state, groupByItems ?? [], defaultCompanionLevelField);
      reconcileCompanionSortField(state, listSort?.view ?? [], defaultCompanionSortField);
    });
    builder.addCase(receiveFlowSheetByStyleConfig, (state, action) => {
      if (hasActiveFavorite(state)) return;
      const { groupByItems, defaultCompanionLevelField, defaultCompanionSortField, listSort } = action.payload;
      state.groupBy = { ...state.groupBy, options: groupByItems ?? [] };
      reconcileCompanionLevelField(state, groupByItems ?? [], defaultCompanionLevelField);
      reconcileCompanionSortField(state, listSort?.view ?? [], defaultCompanionSortField);
    });
    builder.addCase(receiveWorklistConfig, (state, action) => {
      if (hasActiveFavorite(state)) return;
      const { companionView } = action.payload;
      reconcileCompanionSortField(
        state,
        companionView.sortBy?.options ?? [],
        companionView.sortBy?.defaults?.dataIndex,
      );
      reconcileCompanionLevelField(
        state,
        companionView.levelBy?.view ?? [],
        companionView.levelBy?.defaults?.dataIndex,
      );
    });
    builder.addCase(receiveStyleEditConfigs, (state, action) => {
      if (hasActiveFavorite(state)) return;
      const sortBy = action.payload.viewDefn?.companionView?.sortBy;
      reconcileCompanionSortField(state, sortBy?.options ?? [], sortBy?.defaults?.dataIndex);
    });
    builder.addCase(receiveGridViewConfig, (state, action) => {
      if (hasActiveFavorite(state)) return;
      const { sortOptions, defaultSort } = parseSortConfig(action.payload.listSort);
      reconcileCompanionSortField(state, sortOptions, defaultSort?.dataIndex);
    });
    builder.addCase(receiveConfig, (state, action) => {
      if (hasActiveFavorite(state)) return;
      const { sortOptions, defaultSort } = parseSortConfig(action.payload.listSort);
      reconcileCompanionSortField(state, sortOptions, defaultSort?.dataIndex);
    });
    builder.addCase(receiveStyleByLevelOvertimeConfig, (state, action) => {
      if (hasActiveFavorite(state)) return;
      const sortConfig = action.payload.viewDefn?.companionGrid?.sortConfig;
      reconcileCompanionSortField(state, sortConfig?.options ?? [], sortConfig?.defaultSelection);
    });
  },
});

export const {
  requestViewDefns,
  receiveViewDefns,
  updateSearch,
  updateAlternateSearch,
  updateConfigureSelections,
  updateFlowStatus,
  updateFlowStatusConfig,
  updateAlternateFlowStatus,
  updateLookBackPeriod,
  updateGroupBy,
  updateSortBy,
  updatePareDown,
  updateCountLimit,
  updateFavoritesList,
  updateFavorites,
  updateSortByDirection,
  maybeUpdateSortByDirection,
  overloadSubheader,
  receiveError,
  setPossibleBreadcrumbs,
  setExtraPivotParamsConfig,
  setExtraPivotParamsOptions,
  setExtraPivotParamsSelection,
  clearExtraPivotParams,
  cleanUp,
  updateTopMemberSelection,
  receiveTopMemberDropdownData,
  setActiveWorkflowKey,
  updateCompanionSortField,
  updateCompanionSortDirection,
  updateCompanionLevelByField,
} = subheaderSlice.actions;

export const fetchExtraPivotParamsOptions = createAsyncThunk<void, void, { state: AppState }>(
  'subheader/fetchExtraPivotParamsOptions',
  async (_, { getState, dispatch }) => {
    const state = getState();
    const { extraPivotParamsConfig, extraPivotParamsSelections } = state.subheader;

    if (!extraPivotParamsConfig) {
      // Safety check
      return;
    }

    type OptionsResult = { key: string; options: DropdownOption[] };

    const promises: Promise<OptionsResult>[] = Object.keys(extraPivotParamsConfig).map(
      async (key): Promise<OptionsResult> => {
        const param = extraPivotParamsConfig[key];
        const dataApi = param.dataApi;

        try {
          if (dataApi.isListData) {
            // Call listData API
            const res = await ServiceContainer.pivotService.listData(dataApi.defnId, AppType.Assortment, {
              ...dataApi.params,
            });
            const options: DropdownOption[] = res.flat.map((item: any) => ({
              ...item,
              text: item.label, // Add 'text' for SubheaderDropdown
            }));
            return { key, options };
          } else {
            // Handle non-listData APIs
            const values = await getValidValues(getUrl(dataApi));
            const options: DropdownOption[] = values.map((v: any) => ({
              value: v.value,
              label: v.label,
              text: v.label, // Add 'text' for SubheaderDropdown
            }));
            return { key, options };
          }
        } catch (error) {
          logError(`Failed to fetch options for ${key}:`, error);
          return { key, options: [] };
        }
      },
    );

    const results = await Promise.all(promises);
    // Build a map of key to options
    const optionsMap: Record<string, DropdownOption[]> = {};
    results.forEach(({ key, options }) => {
      optionsMap[key] = options;
    });

    // Set all the new options all at once
    dispatch(setExtraPivotParamsOptions(optionsMap));

    // Now, update the selections based on the new options, leaving the pre-selected ones intact
    const updatedSelections: Record<string, string | number | null> = { ...extraPivotParamsSelections };

    Object.keys(optionsMap).forEach((key) => {
      const options = optionsMap[key];
      const currentSelectionValue = extraPivotParamsSelections[key];
      let newSelectionValue: string | number | null = null;

      if (options && options.length > 0) {
        const values = options.map((option) => option.value);

        // Check if current selection value exists in new options values
        if (!isNil(currentSelectionValue) && values.includes(currentSelectionValue)) {
          // Current selection value is valid, keep it
          newSelectionValue = currentSelectionValue;
        } else {
          // Default to first valid option's value
          newSelectionValue = values[0] ?? null;
        }
      } else {
        // No options available, set selection to null - this shouldn't get hit, unless the pivot returns empty
        newSelectionValue = null;
      }

      updatedSelections[key] = newSelectionValue;
    });

    // Dispatch updated selections only for keys in optionsMap, thanks stack overflow
    Object.entries(updatedSelections).forEach(([key, value]) => {
      if (optionsMap.hasOwnProperty(key)) {
        dispatch(setExtraPivotParamsSelection({ key, value }));
      }
    });
  },
);

export default subheaderSlice.reducer;
